import {
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  Typography,
  Menu,
  type SxProps,
  Skeleton,
} from '@mui/material';
import { OnboardingStatus } from '@qdrant/qdrant-cloud-public-api/iam/v1';
import { useNavigate } from '@tanstack/react-router';
import { ChevronsUpDownIcon } from 'lucide-react';
import { Activity, useCallback, type MouseEvent, useState } from 'react';
import { Icon } from '@/components/Icon/Icon';
import { ButtonLink } from '@/router/Link';
import { useAuthenticatedUser, useUserAccounts } from '@/routes/_authenticated.hooks';
import { useSelectedAccount } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { AccountCreationModal } from '../Account/AccountCreationModal';
import { EditAccountDetailsForm } from '../AccountSettings/EditAccountDetailsForm';
import { LinkButton } from '../Common/LinkButton';
import { WithMaxOwnedAccountsQuota } from '../QuotaManagement/WithMaxOwnedAccountsQuota';
import { DashboardAccountCreationAction } from './DashboardAccountCreationAction';
import { DashboardAccountCreationInsufficientQuota } from './DashboardAccountCreationInsufficientQuota';
import { AccountNavigationItems } from './DashboardAccountNavigationItems';
import { DASHBOARD_SIDEBAR_WIDTH } from './DashboardAppBar';

const sxTypography: SxProps = {
  textOverflow: 'ellipsis',
  textWrap: 'nowrap',
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
};

const Nav = styled('nav')`
  display: grid;
  grid-template-rows: auto 0px;
  align-items: start;
  position: relative;
`;

const ToggleButton = styled(ListItemButton)(({ theme }) => ({
  gap: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  outline: `1px solid ${theme.palette.action.selected}`,
  outlineOffset: '-1px',
}));

const NavItemToggleIcon = () => (
  <ListItemIcon sx={{ mr: 'auto' }}>
    <Icon icon={ChevronsUpDownIcon} size={20} />
  </ListItemIcon>
);

const ACCOUNT_MENU_OFFSET_TOP = 36;

export const DashboardAccountNavigation = () => {
  const account = useSelectedAccount();
  const accounts = useUserAccounts();
  const { email, onboardingStatus } = useAuthenticatedUser();
  // The anchor is the element that was clicked, held in state rather than a ref: a ref write does
  // not re-render, so reading `.current` during render can hand `Menu` a stale or missing node.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl !== null;

  const openMenu = useCallback((event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const [creatingAccount, setCreatingAccount] = useState(false);
  const handleAccountCreationModalClose = useCallback(() => {
    setCreatingAccount(false);
  }, []);

  const navigate = useNavigate();
  const accountCount = accounts.length;
  const handleCreateAccountClick = useCallback(() => {
    if (onboardingStatus !== OnboardingStatus.COMPLETED && accountCount === 0) {
      return navigate({ to: '/onboarding/1' });
    }
    setCreatingAccount(true);
    return closeMenu();
  }, [onboardingStatus, navigate, accountCount, closeMenu]);

  return (
    <Nav aria-label="Accounts navigation" sx={{ mb: 1, gridRow: account ? '1' : '1 / span 2' }}>
      {account ? (
        <ToggleButton onClick={openMenu} aria-expanded={open} dense={true} aria-controls="accounts-navigation">
          <ListItemText data-sentry-mask={true} primary={account.name} slotProps={{ primary: { sx: sxTypography } }} />
          <NavItemToggleIcon />
        </ToggleButton>
      ) : (
        <Stack
          sx={{
            bgcolor: 'background.default',
            borderRadius: 1,
            gap: 1,
            py: 1,
            px: 2,
          }}
        >
          <ToggleButton
            disabled={true}
            dense={true}
            sx={{ outlineColor: 'action.hover', color: 'action.disabled', bgcolor: 'action.disabledBackground' }}
          >
            <ListItemText primary="N/A" slotProps={{ primary: { sx: sxTypography } }} />
            <NavItemToggleIcon />
          </ToggleButton>
          <Box sx={{ py: 1, px: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="caption">
              <LinkButton onClick={handleCreateAccountClick} variant="caption">
                Create an account
              </LinkButton>{' '}
              to get started
            </Typography>
          </Box>
        </Stack>
      )}
      <Menu
        id="accounts-navigation"
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          position: 'absolute',
          top: `${ACCOUNT_MENU_OFFSET_TOP}px`,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          transition: {
            timeout: 0,
          },
          paper: {
            elevation: 1,
            sx: (theme) => ({
              outline: `1px solid ${theme.palette.action.hover}`,
              minWidth: parseInt(DASHBOARD_SIDEBAR_WIDTH) - parseInt(theme.spacing(4)),
              mt: 1,
            }),
          },
        }}
      >
        <Stack
          id="accounts-navigation-dropdown"
          sx={{
            gap: 1,
            p: 1,
            color: 'text.secondary',
          }}
        >
          <AccountNavigationItems ownerEmail={email} onItemNavigate={closeMenu} />
          <Divider />
          <WithMaxOwnedAccountsQuota
            show="always"
            fallback={<Skeleton variant="rectangular" height={48} />}
            render={({ available, value, quota }) =>
              available ? (
                <DashboardAccountCreationAction variant="outlined" onCreateAccountClick={handleCreateAccountClick} />
              ) : (
                <Stack
                  sx={{
                    alignItems: 'stretch',
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    gap: 1,
                    justifyContent: 'center',
                    px: 2,
                    py: 1,
                  }}
                >
                  <DashboardAccountCreationAction variant="outlined" disabled={true} />
                  <DashboardAccountCreationInsufficientQuota value={value} quota={quota} />
                </Stack>
              )
            }
          />
          <ButtonLink
            to="/accounts"
            search={{ account_id: account?.id }}
            onClick={closeMenu}
            variant="text"
            color="inherit"
            fullWidth={true}
          >
            Manage accounts
          </ButtonLink>
        </Stack>
      </Menu>
      <Activity mode={creatingAccount ? 'visible' : 'hidden'}>
        <EditAccountDetailsForm>
          <AccountCreationModal onClose={handleAccountCreationModalClose} />
        </EditAccountDetailsForm>
      </Activity>
    </Nav>
  );
};
